import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { BusinessPlanCard } from "@/components/BusinessPlanCard";
import { MediaKitProCard } from "@/components/mediakit/MediaKitProCard";
import { FoundingCreatorBanner } from "@/components/FoundingCreatorBanner";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Inbox, Clock, CheckCircle2, Trophy, Compass, ArrowRight, Sparkles, Star, Megaphone, Target, Check, X, BarChart3, Briefcase, FileText } from "lucide-react";

const APP_STATUS = {
  applied: "bg-amber-100 text-amber-700", under_review: "bg-indigo-100 text-indigo-700",
  accepted: "bg-emerald-100 text-emerald-700", declined: "bg-zinc-200 text-zinc-600", completed: "bg-zinc-200 text-zinc-700",
};

const APP_STATUS_LABEL = { declined: "Not selected" };

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  completed: "bg-zinc-200 text-zinc-700",
};

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "New / pending" },
  { key: "accepted", label: "Accepted" },
  { key: "completed", label: "Completed" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collabs, setCollabs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [showOnboard, setShowOnboard] = useState(false);
  const isBusiness = user?.role === "business";

  useEffect(() => {
    if (user?.id && !localStorage.getItem(`nf_onboard_${user.id}`)) setShowOnboard(true);
  }, [user?.id]);
  const dismissOnboard = () => {
    if (user?.id) localStorage.setItem(`nf_onboard_${user.id}`, "1");
    setShowOnboard(false);
  };

  useEffect(() => {
    api.get("/collaborations").then((r) => setCollabs(r.data.collaborations)).finally(() => setLoading(false));
    if (user?.role === "creator") {
      api.get("/applications/mine").then((r) => setApplications(r.data.applications)).catch(() => {});
      api.get("/invitations/mine").then((r) => setInvitations(r.data.invitations)).catch(() => {});
    }
  }, [user]);

  const respondInvite = async (aid, status) => {
    try {
      const r = await api.post(`/applications/${aid}/respond`, { status });
      if (status === "accepted" && r.data.collaboration_id) { navigate(`/collaborations/${r.data.collaboration_id}`); return; }
      setInvitations((inv) => inv.filter((x) => x.id !== aid));
    } catch { /* noop */ }
  };

  if (user?.role === "admin") return <Navigate to="/admin" replace />;

  const count = (s) => collabs.filter((c) => c.status === s).length;
  const filtered = tab === "all" ? collabs : collabs.filter((c) => c.status === tab);
  const pendingReviews = collabs.filter((c) => c.status === "completed" && !c.reviewed);

  const stats = [
    { label: isBusiness ? "Sent requests" : "New requests", value: count("pending"), Icon: Inbox, tint: "text-amber-600 bg-amber-100" },
    { label: "Accepted", value: count("accepted"), Icon: CheckCircle2, tint: "text-emerald-600 bg-emerald-100" },
    { label: "Pending", value: count("pending"), Icon: Clock, tint: "text-indigo-600 bg-indigo-100" },
    { label: "Completed", value: count("completed"), Icon: Trophy, tint: "text-zinc-600 bg-zinc-200" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span data-testid="dashboard-account-label" className={`mb-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${isBusiness ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
              {isBusiness ? <Briefcase className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              {isBusiness ? "Business Dashboard" : "Creator Dashboard"}
            </span>
            <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Welcome back, {isBusiness ? (user?.name || "there") : (user?.name?.split(" ")[0] || "there")}</h1>
            <p className="mt-1 text-zinc-500">{isBusiness ? "Manage your creator collaborations." : "Manage your incoming collaboration requests."}</p>
          </div>
          {isBusiness ? (
            <div className="flex flex-wrap gap-2">
              <Button data-testid="dashboard-post-opp-btn" onClick={() => navigate("/business/opportunities")} className={`rounded-full bg-emerald-600 hover:bg-emerald-700 ${showOnboard ? "ring-2 ring-emerald-400 ring-offset-2 animate-pulse" : ""}`}>
                <Megaphone className="mr-2 h-4 w-4" /> Post an Opportunity
              </Button>
              <Button data-testid="dashboard-analytics-btn" onClick={() => navigate("/analytics")} variant="outline" className="rounded-full">
                <BarChart3 className="mr-2 h-4 w-4" /> Analytics
              </Button>
              <Button data-testid="dashboard-discover-btn" onClick={() => navigate("/discover")} variant="outline" className="rounded-full">
                <Compass className="mr-2 h-4 w-4" /> Discover
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button data-testid="dashboard-opps-btn" onClick={() => navigate("/opportunities")} className={`rounded-full bg-emerald-600 hover:bg-emerald-700 ${showOnboard ? "ring-2 ring-emerald-400 ring-offset-2 animate-pulse" : ""}`}>
                <Megaphone className="mr-2 h-4 w-4" /> Browse Opportunities
              </Button>
              <Button data-testid="dashboard-analytics-btn" onClick={() => navigate("/analytics")} variant="outline" className="rounded-full">
                <BarChart3 className="mr-2 h-4 w-4" /> Analytics
              </Button>
              <Button data-testid="dashboard-profile-btn" onClick={() => navigate("/creator/profile")} variant="outline" className="rounded-full">
                <Sparkles className="mr-2 h-4 w-4" /> Profile
              </Button>
              <Button data-testid="dashboard-media-kit-btn" onClick={() => navigate("/creator/media-kit")} variant="outline" className="rounded-full">
                <FileText className="mr-2 h-4 w-4" /> Media Kit
              </Button>
            </div>
          )}
        </div>

        {showOnboard && (
          <div data-testid="onboarding-tip" className="mt-4 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white"><Sparkles className="h-4 w-4" /></div>
            <div className="flex-1">
              <p className="font-display font-semibold text-zinc-900">Welcome to Nearfluence 👋</p>
              <p className="mt-0.5 text-sm text-zinc-600">
                {isBusiness
                  ? "Ready to work with local creators? Tap the highlighted \u201CPost an Opportunity\u201D button above to create your first listing."
                  : "Your next collaboration is close by. Tap the highlighted \u201CBrowse Opportunities\u201D button above to find and apply to local gigs."}
              </p>
            </div>
            <button data-testid="onboarding-dismiss" onClick={dismissOnboard} className="shrink-0 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">Got it</button>
          </div>
        )}

        {isBusiness && <BusinessPlanCard />}

        {isBusiness && pendingReviews.length > 0 && (
          <div data-testid="review-reminder-banner" className="mt-6 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
              <Star className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-display font-bold text-amber-900">
                {pendingReviews.length} completed collaboration{pendingReviews.length !== 1 ? "s" : ""} to review
              </p>
              <p className="text-sm text-amber-700">Leave a quick star rating to help other local businesses — and thank your creators.</p>
            </div>
            <Button data-testid="review-reminder-cta" onClick={() => navigate(`/collaborations/${pendingReviews[0].id}`)} className="rounded-full bg-amber-500 hover:bg-amber-600">
              Review now
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl border border-zinc-200 bg-white p-4">
              <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${s.tint}`}><s.Icon className="h-5 w-5" /></div>
              <p className="mt-3 font-display text-2xl font-bold text-zinc-900">{s.value}</p>
              <p className="text-xs text-zinc-500">{s.label}</p>
            </div>
          ))}
        </div>

        {!isBusiness && <FoundingCreatorBanner />}
        {!isBusiness && <MediaKitProCard variant="dashboard" />}

        {/* Invitations (creators) */}
        {!isBusiness && invitations.filter((i) => i.status === "invited").length > 0 && (
          <div className="mt-8" data-testid="my-invitations">
            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-zinc-900"><Target className="h-5 w-5 text-sky-600" /> You've been invited</h2>
            <div className="space-y-3">
              {invitations.filter((i) => i.status === "invited").map((i) => (
                <div key={i.id} data-testid={`invitation-${i.id}`} className="rounded-2xl border border-sky-200 bg-sky-50/60 p-4">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/opportunities/${i.opportunity_id}`)}>
                      <p className="truncate font-display font-semibold text-zinc-900">{i.opportunity?.title || "Opportunity no longer available"}</p>
                      {i.opportunity && <p className="truncate text-sm text-zinc-500">{[i.opportunity.business_name, i.opportunity.location].filter(Boolean).join(" · ")}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`invite-decline-${i.id}`} onClick={() => respondInvite(i.id, "declined")} className="flex h-9 w-9 items-center justify-center rounded-full border border-rose-200 text-rose-500 hover:bg-rose-50"><X className="h-4 w-4" /></button>
                      <button data-testid={`invite-accept-${i.id}`} onClick={() => respondInvite(i.id, "accepted")} className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white hover:bg-emerald-700"><Check className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Applications (creators) */}
        {!isBusiness && applications.length > 0 && (
          <div className="mt-8" data-testid="my-applications">
            <h2 className="mb-3 font-display text-lg font-bold text-zinc-900">My Applications</h2>
            <div className="space-y-3">
              {applications.map((a) => (
                <div key={a.id} data-testid={`application-${a.id}`}
                  className="rounded-2xl border border-zinc-200 bg-white p-4 transition-all hover:shadow-md">
                  <div onClick={() => navigate(a.collaboration_id ? `/collaborations/${a.collaboration_id}` : `/opportunities/${a.opportunity_id}`)}
                    className="flex cursor-pointer items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display font-semibold text-zinc-900">{a.opportunity?.title || "Opportunity no longer available"}</p>
                      {a.opportunity && <p className="truncate text-sm text-zinc-500">{[a.opportunity.business_name, a.opportunity.location].filter(Boolean).join(" · ")}</p>}
                    </div>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${APP_STATUS[a.status] || "bg-zinc-100 text-zinc-600"}`}>{APP_STATUS_LABEL[a.status] || a.status.replace("_", " ")}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-zinc-300" />
                  </div>
                  {a.status === "declined" && (
                    <div data-testid={`application-feedback-${a.id}`} className="mt-3 rounded-xl bg-zinc-50 p-3 text-sm">
                      {(a.decline_reason_label || a.decline_message) ? (
                        <div className="space-y-1.5 text-zinc-700">
                          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Feedback from the business</p>
                          {a.decline_reason_label && <p><span className="font-medium text-zinc-900">Reason:</span> {a.decline_reason_label}</p>}
                          {a.decline_message && <p className="whitespace-pre-line">{a.decline_message}</p>}
                        </div>
                      ) : null}
                      <p className={`text-xs text-zinc-500 ${(a.decline_reason_label || a.decline_message) ? "mt-2" : ""}`}>
                        The business made their selection and didn't choose your application this time. This isn't a reflection of your worth as a creator — keep exploring, more opportunities are waiting.
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`dashboard-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
          ) : filtered.length === 0 ? (
            <div data-testid="dashboard-empty" className="flex h-48 flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 text-center">
              <Inbox className="h-8 w-8 text-zinc-300" />
              <p className="mt-2 font-display font-semibold text-zinc-700">No collaborations here yet</p>
              <p className="mt-1 text-sm text-zinc-500">
                {isBusiness ? "Discover creators and send your first request." : "When businesses reach out, requests will appear here."}
              </p>
            </div>
          ) : (
            filtered.map((c) => {
              const name = isBusiness ? c.creator_name : c.business_name;
              const photo = isBusiness ? c.creator_photo : c.business_logo;
              return (
                <div key={c.id} data-testid={`collab-row-${c.id}`} onClick={() => navigate(`/collaborations/${c.id}`)}
                  className="flex cursor-pointer items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4 transition-all hover:shadow-md">
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
                    {photo ? <img src={fileUrl(photo)} alt={name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{name?.[0]}</div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold text-zinc-900">{c.campaign_title}</p>
                    <p className="truncate text-sm text-zinc-500">{isBusiness ? "To" : "From"} {name} · {c.content_type || c.platform}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[c.status]}`}>{c.status}</span>
                    {c.proposed_payment != null && <span className="font-mono text-sm font-semibold text-zinc-700">£{c.proposed_payment}</span>}
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-zinc-300" />
                </div>
              );
            })
          )}
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
